import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SmLogoWhiteComponent } from 'src/app/components/sm-logo/sm-logo-white.component';

export type RechargeTicketStatus =
  | 'pending'
  | 'paid'
  | 'confirmed'
  | 'rejected'
  | 'expired';

export interface RechargeTicketData {
  id?: string;
  status: RechargeTicketStatus;
  amount: number;
  currency: string;            // e.g. "USDT"
  network?: string;            // e.g. "TRC-20"
  walletAddress?: string;
  targetAccount?: string;      // internal SuperPay account being credited
  fiatEquivalent?: { amount: number; currency: string };
  reference?: string;
  expiresAt?: string;          // ISO
  createdAt?: string;
  issuedAt?: string;           // ISO – date displayed in the header
  note?: string;
  customerName?: string;
  customerEmail?: string;
  baseAmount?: number;         // recharge amount before fee (in baseCurrency)
  baseCurrency?: string;       // currency for baseAmount/feeAmount (e.g. "USD")
  feePercent?: number;
  feeAmount?: number;
}

@Component({
  selector: 'app-recharge-ticket',
  standalone: true,
  imports: [CommonModule, SmLogoWhiteComponent],
  templateUrl: './recharge-ticket.component.html',
  styleUrl: './recharge-ticket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RechargeTicketComponent {
  @Input({ required: true }) ticket!: RechargeTicketData;

  @Output() markPaid = new EventEmitter<void>();
  @Output() viewDetails = new EventEmitter<void>();

  private snackBar = inject(MatSnackBar);

  copied = signal(false);
  private now = signal(Date.now());
  private nowTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit() {
    if (this.needsTimer()) {
      this.startTimer();
    }
  }

  ngOnDestroy() {
    this.stopTimer();
  }

  private needsTimer(): boolean {
    if (!this.ticket?.expiresAt) return false;
    if (this.ticket.status !== 'pending') return false;
    return new Date(this.ticket.expiresAt).getTime() > Date.now();
  }

  private startTimer() {
    if (this.nowTimer) return;
    this.nowTimer = setInterval(() => {
      this.now.set(Date.now());
      if (!this.needsTimer()) this.stopTimer();
    }, 1000);
  }

  private stopTimer() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
      this.nowTimer = null;
    }
  }

  readonly statusLabel = computed<string>(() => {
    switch (this.ticket?.status) {
      case 'pending':
        return 'Pendiente de pago';
      case 'paid':
        return 'Pagado · esperando confirmación';
      case 'confirmed':
        return 'Confirmado';
      case 'rejected':
        return 'Rechazado';
      case 'expired':
        return 'Vencido';
      default:
        return '';
    }
  });

  readonly remainingLabel = computed<string | null>(() => {
    if (!this.ticket?.expiresAt) return null;
    const ms = new Date(this.ticket.expiresAt).getTime() - this.now();
    if (ms <= 0) return 'Vencido';
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const s = Math.floor((ms % 60000) / 1000);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
    return `${s}s`;
  });

  // Base amount before fee (in baseCurrency). Falls back to `amount`.
  readonly baseAmount = computed<number>(
    () => this.ticket?.baseAmount ?? this.ticket?.amount ?? 0
  );

  readonly baseCurrency = computed<string>(
    () => this.ticket?.baseCurrency ?? this.ticket?.fiatEquivalent?.currency ?? 'USD'
  );

  readonly feePercent = computed<number | null>(() => {
    const p = this.ticket?.feePercent;
    return typeof p === 'number' ? p : null;
  });

  readonly feeAmount = computed<number>(() => {
    const f = this.ticket?.feeAmount;
    if (typeof f === 'number') return f;
    const p = this.ticket?.feePercent;
    const base = this.ticket?.baseAmount;
    if (typeof p === 'number' && typeof base === 'number') return (base * p) / 100;
    return 0;
  });

  readonly issuedDateLabel = computed<string>(() => {
    const iso = this.ticket?.issuedAt ?? this.ticket?.createdAt;
    const d = iso ? new Date(iso) : new Date();
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd} / ${mm} / ${yyyy}`;
  });

  readonly totalIntPart = computed<string>(() => this.splitAmount(this.ticket?.amount ?? 0).int);
  readonly totalDecPart = computed<string>(() => this.splitAmount(this.ticket?.amount ?? 0).dec);

  // Formats with Spanish thousands separator (space) and comma decimal.
  // e.g. 7966.42 → "7 966,42"
  formatEuro(value: number, decimals = 2): string {
    if (!isFinite(value)) value = 0;
    const fixed = value.toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decimals > 0 ? `${grouped},${decPart}` : grouped;
  }

  // Splits 8087.74 → { int: "8 087", dec: "74" } for the big total display.
  splitAmount(value: number): { int: string; dec: string } {
    const f = this.formatEuro(value, 2);
    const [int, dec] = f.split(',');
    return { int, dec: dec ?? '00' };
  }

  // Builds the chip text "USDT · TRC20" (or just currency if no network).
  readonly networkChipLabel = computed<string>(() => {
    const c = this.ticket?.currency ?? '';
    const n = (this.ticket?.network ?? '').replace(/-/g, '');
    return n ? `${c} · ${n}` : c;
  });

  // Sub-label below the big total. "Red Tron · USDT" for TRC20, otherwise
  // falls back to "Red <network> · <currency>".
  readonly networkSubLabel = computed<string>(() => {
    const c = this.ticket?.currency ?? '';
    const raw = (this.ticket?.network ?? '').toUpperCase();
    let net = raw.replace(/-/g, '');
    if (net === 'TRC20') net = 'Tron';
    else if (net === 'ERC20') net = 'Ethereum';
    else if (net === 'BEP20') net = 'BSC';
    return raw ? `Red ${net} · ${c}` : c;
  });

  // "SP-RC-2026-05-12-093034" → "SP · RC · 2026 · 05 · 12 · 093034"
  formatBarcodeLabel(reference: string): string {
    return reference.replace(/[-_/]/g, ' · ');
  }

  truncateAddress(addr: string): string {
    if (!addr) return '';
    if (addr.length <= 16) return addr;
    return `${addr.slice(0, 8)}…${addr.slice(-6)}`;
  }

  async copyAddress() {
    if (!this.ticket.walletAddress) return;
    await this.copyText(this.ticket.walletAddress, () => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1800);
    });
  }

  async copyReference() {
    if (!this.ticket.reference) return;
    await this.copyText(this.ticket.reference, () =>
      this.snackBar.open('Referencia copiada', 'Ok', { duration: 1200 })
    );
  }

  async copyAccount() {
    if (!this.ticket.targetAccount) return;
    await this.copyText(this.ticket.targetAccount, () =>
      this.snackBar.open('Cuenta copiada', 'Ok', { duration: 1200 })
    );
  }

  private async copyText(value: string, onOk: () => void) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(ta);
        }
      }
      onOk();
    } catch {
      this.snackBar.open('No se pudo copiar.', 'Ok', { duration: 2500 });
    }
  }
}
